/**
 * Full video probe via mediabunny. Demuxes the container and reads track
 * metadata using WebCodecs-style APIs. No wasm. Lazy-loaded.
 *
 * Mediabunny surfaces coded/display dimensions, pixel aspect, color space
 * (primaries/transfer/matrix/range — from the bitstream when the container
 * carries none), HDR, flip, per-track codec config (profile+level embedded in
 * the WebCodecs codec string), duration, bitrate (container metadata, else
 * packet stats), and frame-rate metrics that tell CFR from VFR.
 */

import type * as Mediabunny from 'mediabunny';
import type {
  AudioCodec,
  AudioStreamInfo,
  ChromaSubsampling,
  H264Profile,
  VideoCodec,
  VideoProbeReport,
  VideoStreamInfo,
} from './types';

export interface MediabunnyProbeResult {
  video: VideoProbeReport['video'];
  audio: VideoProbeReport['audio'];
  durationSec: number;
  warnings: VideoProbeReport['warnings'];
  bailout: VideoProbeReport['bailout'];
}

type MediabunnyModule = typeof Mediabunny;

/** Packets sampled for bitrate when the container carries no bitrate metadata. */
const PACKET_STATS_SAMPLE = 200;

let moduleCache: MediabunnyModule | null = null;

async function loadModule(): Promise<MediabunnyModule> {
  if (moduleCache) return moduleCache;
  try {
    // No @vite-ignore: Vite 8 (Rolldown) leaves an ignored bare specifier
    // unbundled, which apps bundling this source can't resolve at runtime. The
    // library build lists mediabunny in `external`, so dist keeps it a peer.
    moduleCache = await import('mediabunny');
    return moduleCache;
  } catch (err) {
    throw new Error(
      `Failed to load mediabunny. Install it as a dependency: npm i mediabunny. ${(err as Error).message ?? ''}`,
    );
  }
}

export async function probeWithMediabunny(input: Blob): Promise<MediabunnyProbeResult> {
  const warnings: VideoProbeReport['warnings'] = [];
  let mod: MediabunnyModule;
  try {
    mod = await loadModule();
  } catch (err) {
    return {
      video: null,
      audio: null,
      durationSec: 0,
      warnings: [{ code: 'probe-load-failed', message: (err as Error).message ?? 'unknown error' }],
      bailout: 'probe-failed',
    };
  }

  let mbInput: Mediabunny.Input;
  try {
    mbInput = new mod.Input({ source: new mod.BlobSource(input), formats: mod.ALL_FORMATS });
  } catch (err) {
    return {
      video: null,
      audio: null,
      durationSec: 0,
      warnings: [{ code: 'probe-demux-failed', message: (err as Error).message ?? 'Mediabunny could not open the file.' }],
      bailout: 'probe-failed',
    };
  }

  try {
    const duration = await mbInput.computeDuration();
    const videoTrack = await mbInput.getPrimaryVideoTrack();
    const audioTrack = await mbInput.getPrimaryAudioTrack();

    const video = videoTrack ? await mapVideoTrack(videoTrack, duration, warnings) : null;
    const audio = audioTrack ? await mapAudioTrack(audioTrack) : null;

    return { video, audio, durationSec: duration, warnings, bailout: null };
  } catch (err) {
    return {
      video: null,
      audio: null,
      durationSec: 0,
      warnings: [{ code: 'probe-read-failed', message: (err as Error).message ?? 'Probe failed while reading tracks.' }],
      bailout: 'probe-failed',
    };
  } finally {
    mbInput.dispose();
  }
}

async function mapVideoTrack(track: Mediabunny.InputVideoTrack, containerDuration: number, warnings: VideoProbeReport['warnings']): Promise<VideoStreamInfo> {
  const [mbCodec, codedWidth, codedHeight, displayWidth, displayHeight, pixelAspectRatio] = await Promise.all([
    track.getCodec(),
    track.getCodedWidth(),
    track.getCodedHeight(),
    track.getDisplayWidth(),
    track.getDisplayHeight(),
    track.getPixelAspectRatio(),
  ]);
  const codec = mapVideoCodec(mbCodec);
  const colorSpace = await track.getColorSpace().catch((): VideoColorSpaceInit => ({}));
  const decoderConfig = await track.getDecoderConfig().catch(() => null);
  const codecString = decoderConfig?.codec ?? (await track.getCodecParameterString().catch(() => null));
  const hdr = await track.hasHighDynamicRange().catch(() => false);
  const trackDuration = await track.computeDuration().catch(() => containerDuration);
  const fps = await track.computeFrameRateMetrics().catch(() => null);
  const bitrateBps = await trackBitrate(track);

  if (await track.getFlip().catch(() => false)) {
    warnings.push({ code: 'flipped', message: 'Video carries mirror (flip) metadata; some players ignore it and show the unflipped image.' });
  }

  const sar = rationalToString(pixelAspectRatio);
  const isAnamorphic = sar != null && sar !== '1:1';
  const dar = displayWidth && displayHeight
    ? reduceRatio(displayWidth, displayHeight)
    : null;
  const evenDimensions = codedWidth % 2 === 0 && codedHeight % 2 === 0;

  const { profile, level } = codec === 'h264' ? parseAvcCodecString(codecString) : { profile: null, level: null };

  if (bitrateBps == null) {
    warnings.push({ code: 'bitrate-unknown', message: 'Could not determine bitrate from container metadata or packet stats.' });
  }

  return {
    codec,
    codecFourcc: fourccForCodec(codec),
    width: displayWidth || codedWidth,
    height: displayHeight || codedHeight,
    codedWidth,
    codedHeight,
    sampleAspectRatio: sar,
    displayAspectRatio: dar,
    isAnamorphic,
    evenDimensions,
    durationSec: trackDuration,
    framerate: fps?.bestGuessFrameRate ?? null,
    // frameRateIsConstant is false when frames are skipped (phone low-light
    // capture) or no uniform lattice fits. Mediabunny's lattice fit is loose:
    // timestamp jitter and some mixed-rate edits still read as constant.
    framerateMode: fps == null ? 'unknown' : fps.frameRateIsConstant ? 'cfr' : 'vfr',
    bitrateBps,
    profile,
    level,
    pixelFormat: colorSpaceToPixelFormat(colorSpace),
    chromaSubsampling: guessChromaSubsampling(colorSpace),
    // Mediabunny does not expose bit depth. Reporting null keeps evalBitDepth
    // silent (skipped axis) rather than asserting 8-bit on a 10/12-bit source.
    bitDepth: null,
    colorRange: colorSpace.fullRange === true ? 'pc' : colorSpace.fullRange === false ? 'tv' : null,
    hasB: null,
    colorPrimaries: colorSpace.primaries ?? null,
    hdr,
  };
}

async function mapAudioTrack(track: Mediabunny.InputAudioTrack): Promise<AudioStreamInfo> {
  const [mbCodec, channels, sampleRateHz] = await Promise.all([
    track.getCodec(),
    track.getNumberOfChannels(),
    track.getSampleRate(),
  ]);
  const codec = mapAudioCodec(mbCodec);
  return {
    codec,
    codecFourcc: audioFourccForCodec(codec),
    channels,
    sampleRateHz,
    bitrateBps: await trackBitrate(track),
  };
}

/**
 * Whole-file average bitrate from container metadata (ISOBMFF `btrt`) when
 * present — exact and free. Otherwise estimate from a packet-prefix sample.
 */
async function trackBitrate(track: Mediabunny.InputTrack): Promise<number | null> {
  const fromMetadata = await track.getAverageBitrate().catch(() => null);
  if (fromMetadata) return fromMetadata;
  const stats = await track.computePacketStats(PACKET_STATS_SAMPLE).catch(() => null);
  return stats?.averageBitrate || null;
}

function mapVideoCodec(c: Mediabunny.VideoCodec | null): VideoCodec {
  switch (c) {
    case 'avc':
      return 'h264';
    case 'hevc':
      return 'hevc';
    case 'vp9':
      return 'vp9';
    case 'av1':
      return 'av1';
    default:
      return 'unknown';
  }
}

function mapAudioCodec(c: Mediabunny.AudioCodec | null): AudioCodec {
  switch (c) {
    case 'aac': return 'aac';
    case 'mp3': return 'mp3';
    case 'opus': return 'opus';
    case 'vorbis': return 'vorbis';
    case 'ac3': return 'ac3';
    default: return 'unknown';
  }
}

function fourccForCodec(codec: VideoCodec): string {
  switch (codec) {
    case 'h264': return 'avc1';
    case 'hevc': return 'hvc1';
    case 'vp9': return 'vp09';
    case 'av1': return 'av01';
    default: return '';
  }
}

function audioFourccForCodec(codec: AudioCodec): string {
  switch (codec) {
    case 'aac': return 'mp4a';
    case 'mp3': return 'mp3 ';
    case 'opus': return 'Opus';
    default: return '';
  }
}

function rationalToString(r: Mediabunny.Rational | null | undefined): string | null {
  if (!r || !r.num || !r.den) return null;
  const g = gcd(Math.abs(r.num), Math.abs(r.den));
  return `${r.num / g}:${r.den / g}`;
}

function reduceRatio(a: number, b: number): string {
  const g = gcd(Math.abs(a), Math.abs(b)) || 1;
  return `${a / g}:${b / g}`;
}

function gcd(x: number, y: number): number {
  return y === 0 ? x : gcd(y, x % y);
}

function parseAvcCodecString(codec: string | null | undefined): { profile: string | null; level: string | null } {
  if (!codec) return { profile: null, level: null };
  const m = /^avc1\.([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(codec);
  if (!m) return { profile: null, level: null };
  const profileIdc = parseInt(m[1], 16);
  const levelIdc = parseInt(m[3], 16);
  return { profile: profileIdcToName(profileIdc), level: levelIdcToString(levelIdc) };
}

function profileIdcToName(idc: number): string | null {
  switch (idc) {
    case 66: return 'Baseline' satisfies H264Profile;
    case 77: return 'Main' satisfies H264Profile;
    case 88: return 'Extended';
    case 100: return 'High' satisfies H264Profile;
    case 110: return 'High 10';
    case 122: return 'High 4:2:2';
    case 244: return 'High 4:4:4';
    default: return null;
  }
}

function levelIdcToString(idc: number): string {
  const major = Math.floor(idc / 10);
  const minor = idc % 10;
  return `${major}.${minor}`;
}

function colorSpaceToPixelFormat(cs: VideoColorSpaceInit): string | null {
  // Mediabunny surfaces color space metadata, not raw pix_fmt. When we see
  // any color-matrix metadata we report 'yuv420p' (the overwhelmingly common
  // case for H.264 mp4); when the file has no color metadata at all we return
  // null and let the UI/verdict treat it as unknown.
  const matrix = (cs.matrix ?? '').toLowerCase();
  if (matrix) return 'yuv420p';
  return null;
}

function guessChromaSubsampling(cs: VideoColorSpaceInit): ChromaSubsampling {
  // Mediabunny does not expose chroma subsampling directly. For the web-AR
  // target audience (h264 mp4), assume yuv420p; flag it as unknown only when
  // we have no color-space metadata at all.
  if (cs.matrix == null && cs.primaries == null && cs.transfer == null) return 'unknown';
  return 'yuv420p';
}

